import React, { useState, useEffect } from 'react';
import { Form, Select, message, Typography, Spin, InputNumber, Button, Table, Input, App } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { PageTitle } from '../../common';

const { Option } = Select;

interface SectorData {
  id: number;
  descripcion: string;
}

interface EquipoData {
  id: number;
  descripcion: string;
  id_sector: number;
}

interface MaquinariaData {
  id: number;
  descripcion: string;
  id_equipo: number;
}

interface InsumoData {
  id: number;
  descripcion: string;
  precio_promedio: number;
}

interface TablaEgresoData {
  key: number;
  insumo_id: number;
  insumo: string;
  cantidad: number;
  precio: number;
  subtotal: number;
}

interface PadronData {
  id: number;
  razon: string;
}

function CrearEgresos() {
  const { message: messageApi } = App.useApp();
  const [form] = Form.useForm();
  const { getAccessTokenSilently, user } = useAuth0();
  const empresa = user?.id_empresa;
  const sucursal = user?.id_sucursal;

  const [sectores, setSectores] = useState<SectorData[]>([]);
  const [equipos, setEquipos] = useState<EquipoData[]>([]);
  const [maquinarias, setMaquinarias] = useState<MaquinariaData[]>([]);
  const [insumos, setInsumos] = useState<InsumoData[]>([]);
  const [loadingInsumos, setLoadingInsumos] = useState<boolean>(false);
  const [selectedSector, setSelectedSector] = useState<number>();
  const [selectedEquipo, setSelectedEquipo] = useState<number>();
  const [loading, setLoading] = useState<boolean>(false);
  const [cantidad, setCantidad] = useState<number>(0);
  const [datosTabla, setDatosTabla] = useState<TablaEgresoData[]>([]);
  const [padrones, setPadrones] = useState<PadronData[]>([]);
  const [loadingPadrones, setLoadingPadrones] = useState<boolean>(false);
  const [enviandoDatos, setEnviandoDatos] = useState<boolean>(false);
  const [stockActual, setStockActual] = useState<number>(0);
  const navigate = useNavigate();
  // Cargar sectores
  const fetchSectores = async () => {
    const messageKey = 'loadingSectores';
    try {
      setLoading(true);
      messageApi.loading({ content: 'Cargando Sectores...', key: messageKey, duration: 0 });
      
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/listar_sectores/${empresa}/${sucursal}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar sectores');
      }

      const data = await response.json();
      setSectores(data);
      messageApi.success({ content: 'Sectores cargados correctamente', key: messageKey });
    } catch (error) {
      console.error('Error:', error);
      messageApi.error({ content: 'Error al cargar los sectores', key: messageKey });
    } finally {
      setLoading(false);
    }
  };

  // Cargar equipos basados en el sector seleccionado
  const fetchEquipos = async (sectorId: number) => {
    const messageKey = 'loadingEquipos';
    try {
      setLoading(true);
      messageApi.loading({ content: 'Cargando Equipos...', key: messageKey, duration: 0 });

      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/listar_equipos_sectores/${empresa}/${sucursal}/${sectorId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar equipos');
      }

      const data = await response.json();
      setEquipos(data);
      messageApi.success({ content: 'Equipos cargados correctamente', key: messageKey });
    } catch (error) {
      console.error('Error:', error);
      messageApi.error({ content: 'Error al cargar los equipos', key: messageKey });
    } finally {
      setLoading(false);
    }
  };

  // Cargar maquinarias basadas en el equipo seleccionado
  const fetchMaquinarias = async (equipoId: number) => {
    const messageKey = 'loadingMaquinarias';
    try {
      setLoading(true);
      messageApi.loading({ content: 'Cargando Maquinarias...', key: messageKey, duration: 0 });

      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/listar_maquinaria_equipos_sectores/${empresa}/${sucursal}/${selectedSector}/${equipoId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar maquinarias');
      }

      const data = await response.json();
      setMaquinarias(data);
      messageApi.success({ content: 'Maquinarias cargadas correctamente', key: messageKey });
    } catch (error) {
      console.error('Error:', error);
      messageApi.error({ content: 'Error al cargar las maquinarias', key: messageKey });
    } finally {
      setLoading(false);
    }
  };

  // Función para cargar insumos
  const fetchInsumos = async () => {
    const messageKey = 'loadingInsumos';
    try {
      setLoadingInsumos(true);
      messageApi.loading({ content: 'Cargando Insumos...', key: messageKey, duration: 0 });

      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/listar_insumos/${empresa}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar insumos');
      }

      const data = await response.json();
      setInsumos(data);
      messageApi.success({ content: 'Insumos cargados correctamente', key: messageKey });
    } catch (error) {
      console.error('Error:', error);
      messageApi.error({ content: 'Error al cargar los insumos', key: messageKey });
    } finally {
      setLoadingInsumos(false);
    }
  };

  // Cargar padrones
  const fetchPadrones = async () => {
    try {
      setLoadingPadrones(true);
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/listar_padron/${empresa}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar retiros');
      }

      const data = await response.json();
      console.log('padrones', data);
      setPadrones(data);
    } catch (error) {
      console.error('Error:', error);
      messageApi.error('Error al cargar los retiros');
    } finally {
      setLoadingPadrones(false);
    }
  };

  // Cargar padrones después de insumos
  useEffect(() => {
    if (empresa && insumos.length > 0) {
      fetchPadrones();
    }
  }, [empresa, insumos]);

  // Cargar sectores al montar el componente
  useEffect(() => {
    if (empresa) {
      fetchSectores();
      fetchInsumos();
    }
  }, [empresa]);

  // Manejadores de cambios
  const handleSectorChange = (value: number) => {
    console.log('Sector seleccionado:', value);
    setSelectedSector(value);
    setSelectedEquipo(undefined);
    form.setFieldsValue({ 
      equipo: undefined, 
      maquinaria: undefined 
    });
    
    if (value) {
      fetchEquipos(value);
    } else {
      setEquipos([]);
    }
  };

  const handleEquipoChange = (value: number) => {
    console.log('Equipo seleccionado:', value);
    setSelectedEquipo(value);
    form.setFieldsValue({ maquinaria: undefined });
    
    if (value) {
      fetchMaquinarias(value);
    } else {
      setMaquinarias([]);
    }
  };

  // Modificar el manejador de cambio de maquinaria para cargar insumos
  const handleMaquinariaChange = (value: number) => {
    console.log('Maquinaria seleccionada:', value);
  };

  // Manejadores para los nuevos campos
  const handleCantidadChange = (value: number | null) => {
    const newCantidad = value || 0;
    setCantidad(newCantidad);
    form.setFieldsValue({ 
      cantidad: newCantidad 
    });
  };

  // Función para agregar datos a la tabla
  const agregarATabla = () => {
    const insumoSeleccionado = insumos.find(i => i.id === form.getFieldValue('insumo'));
    
    if (!insumoSeleccionado) {
      messageApi.error('Por favor seleccione un insumo');
      return;
    }

    const nuevoRegistro: TablaEgresoData = {
      key: Date.now(),
      insumo_id: insumoSeleccionado.id,
      insumo: insumoSeleccionado.descripcion,
      cantidad: form.getFieldValue('cantidad') || 0,
      precio: 0,
      subtotal: 0
    };

    setDatosTabla([...datosTabla, nuevoRegistro]);

    // Limpiar los campos del formulario
    form.setFieldsValue({
      insumo: undefined,
      cantidad: null
    });

    // Resetear estados
    setCantidad(0);
  };

  // Función para eliminar un registro
  const eliminarRegistro = (key: number) => {
    setDatosTabla(datosTabla.filter(item => item.key !== key));
  };

  // Columnas de la tabla con estilo en los encabezados
  const columns = [
    {
      title: 'Insumo',
      dataIndex: 'insumo',
      key: 'insumo',
      className: 'header-column',
    },
    {
      title: 'Cantidad',
      dataIndex: 'cantidad',
      key: 'cantidad',
      align: 'right' as const,
      className: 'header-column',
    },
    {
      title: 'Acción',
      key: 'accion',
      align: 'center' as const,
      className: 'header-column',
      render: (_: any, record: TablaEgresoData) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => eliminarRegistro(record.key)}
        />
      ),
    },
  ];

  // Función para reiniciar el formulario manteniendo sectores e insumos
  const reiniciarFormulario = () => {
    form.resetFields(['sector', 'equipo', 'maquinaria', 'insumo', 'cantidad', 'retiro', 'observaciones']);
    setSelectedEquipo(undefined);
    setEquipos([]);
    setMaquinarias([]);
    setCantidad(0);
    setDatosTabla([]);
    setStockActual(0);
  };

  // Modificar finalizarEgreso para usar reiniciarFormulario
  const finalizarEgreso = async () => {
    const messageKey = 'finalizandoEgreso';
    try {
      if (datosTabla.length === 0) {
        messageApi.error('Debe agregar al menos un insumo');
        return;
      }

      const values = await form.validateFields();
      
      setEnviandoDatos(true);
      messageApi.loading({ content: 'Guardando egreso...', key: messageKey, duration: 0 });

      const datosEgreso = {
        empresa,
        sucursal,
        sector: values.sector,
        equipo: values.equipo,
        maquinaria: values.maquinaria,
        retiro: values.retiro,
        observaciones: values.observaciones || '',
        items: datosTabla.map(item => ({
          insumo_id: item.insumo_id,
          cantidad: item.cantidad,
          precio: 0
        }))
      };

      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/crear_egreso`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(datosEgreso)
      });

      if (!response.ok) {
        throw new Error('Error al guardar el egreso');
      }

      messageApi.success({ content: 'Egreso guardado correctamente', key: messageKey });
      
      // Usar la nueva función de reinicio
      reiniciarFormulario();

    } catch (error) {
      console.error('Error:', error);
      messageApi.error({ 
        content: error instanceof Error ? error.message : 'Error al guardar el egreso', 
        key: messageKey 
      });
    } finally {
      setEnviandoDatos(false);
    }
  };

  // Modificar el botón de Cancelar para usar reiniciarFormulario
  const handleCancelar = () => {
    reiniciarFormulario();
  };

  // Función para obtener el stock del insumo
  const fetchStockInsumo = async (insumoId: number) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_INSUMOS_URL}/stock_insumos/${empresa}/${sucursal}/${insumoId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Error al obtener el stock');
      }

      const stock = await response.json();
      setStockActual(stock);
    } catch (error) {
      console.error('Error:', error);
      messageApi.error('Error al obtener el stock del insumo');
    }
  };

  // Modificar el handleInsumoChange para incluir la llamada al precio promedio
  const handleInsumoChange = async (value: number) => {
    if (value) {
      await fetchStockInsumo(value);
      
      // Resetear cantidad y recalcular subtotal
      const newCantidad = 0;
      
      form.setFieldsValue({
        cantidad: newCantidad
      });
      
      setCantidad(newCantidad);
    } else {
      setStockActual(0);
      setCantidad(0);
      form.setFieldsValue({
        cantidad: 0
      });
    }
  };

  return (
    <div style={{ padding: '0' }}>
      <style>{`
        /* Asegurar altura consistente para todos los inputs y selects */
        .crear-egresos-form .ant-select-selector,
        .crear-egresos-form .ant-input,
        .crear-egresos-form .ant-input-number-input {
          height: 28px !important;
          line-height: 28px !important;
        }
        .crear-egresos-form .ant-select-selection-item,
        .crear-egresos-form .ant-select-selection-placeholder {
          line-height: 26px !important;
        }
      `}</style>
      
      <PageTitle level={3} style={{ fontSize: '18px', marginBottom: '12px', textAlign: 'center' }}>
        CREAR EGRESOS
      </PageTitle>

      <Form
        form={form}
        layout="horizontal"
        className="crear-egresos-form"
        style={{ maxWidth: '1200px', margin: '0 auto' }}
      >
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
          {/* Selector de Sección */}
          <Form.Item
            label={<span style={{ fontSize: '11px', fontWeight: 500 }}>Sección</span>}
            name="sector"
            rules={[{ required: true, message: 'Por favor seleccione una sección' }]}
            style={{ flex: 1, minWidth: '200px', marginBottom: 0 }}
          >
            <Select
              size="small"
              placeholder="Seleccione una sección"
              onChange={handleSectorChange}
              loading={loading}
              showSearch
              optionFilterProp="children"
              style={{ fontSize: '11px', height: '28px' }}
            >
              {sectores.map(sector => (
                <Option key={sector.id} value={sector.id}>
                  {sector.descripcion}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Selector de Equipo */}
          <Form.Item
            label={<span style={{ fontSize: '11px', fontWeight: 500 }}>Equipo</span>}
            name="equipo"
            rules={[{ required: true, message: 'Por favor seleccione un equipo' }]}
            style={{ flex: 1, minWidth: '200px', marginBottom: 0 }}
          >
            <Select
              size="small"
              placeholder="Seleccione un equipo"
              onChange={handleEquipoChange}
              loading={loading}
              disabled={!selectedSector}
              showSearch
              optionFilterProp="children"
              style={{ fontSize: '11px', height: '28px' }}
            >
              {equipos.map(equipo => (
                <Option key={equipo.id} value={equipo.id}>
                  {equipo.descripcion}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Selector de Maquinaria */}
          <Form.Item
            label={<span style={{ fontSize: '11px', fontWeight: 500 }}>Maquinaria</span>}
            name="maquinaria"
            rules={[{ required: true, message: 'Por favor seleccione una maquinaria' }]}
            style={{ flex: 1, minWidth: '200px', marginBottom: 0 }}
          >
            <Select
              size="small"
              placeholder="Seleccione una maquinaria"
              loading={loading}
              disabled={!selectedEquipo}
              showSearch
              optionFilterProp="children"
              onChange={handleMaquinariaChange}
              style={{ fontSize: '11px', height: '28px' }}
            >
              {maquinarias.map(maquinaria => (
                <Option key={maquinaria.id} value={maquinaria.id}>
                  {maquinaria.descripcion}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        {/* Título centrado */}
        <div style={{ 
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: '8px'
        }}>
          <h3 style={{ fontSize: '13px', fontWeight: 600, margin: 0 }}>
            Insumos
          </h3>
        </div>

        {/* Contenedor para inputs y tabla */}
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          marginBottom: '12px'
        }}>
          {/* Contenedor de inputs - 50% del ancho */}
          <div style={{ width: '50%' }}>
            <Form.Item
              label={<span style={{ fontSize: '11px', fontWeight: 500 }}>Insumo</span>}
              name="insumo"
              style={{ marginBottom: '8px' }}
            >
              <Select
                size="small"
                placeholder="Seleccione un insumo"
                showSearch
                optionFilterProp="children"
                loading={loadingInsumos}
                disabled={!maquinarias.length}
                onChange={handleInsumoChange}
                style={{ fontSize: '11px', height: '28px' }}
              >
                {insumos.map(insumo => (
                  <Option key={insumo.id} value={insumo.id}>
                    {insumo.descripcion}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
              <Form.Item
                label={<span style={{ fontSize: '11px', fontWeight: 500 }}>Stock</span>}
                style={{ marginBottom: 0, flex: 1 }}
              >
                <InputNumber
                  size="small"
                  style={{
                    width: '100%',
                    backgroundColor: '#f5f5f5',
                    fontWeight: 'bold',
                    textAlign: 'right',
                    fontSize: '11px',
                    height: '28px'
                  }}
                  disabled
                  value={stockActual}
                  precision={2}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  className="stock-input"
                />
              </Form.Item>

              <Form.Item
                label={<span style={{ fontSize: '11px', fontWeight: 500 }}>Cantidad</span>}
                name="cantidad"
                style={{ marginBottom: 0, flex: 1 }}
              >
                <InputNumber
                  size="small"
                  style={{ width: '100%', fontSize: '11px', height: '28px' }}
                  min={0}
                  precision={2}
                  onChange={handleCantidadChange}
                  placeholder="0.00"
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={agregarATabla}
                  disabled={!cantidad}
                  style={{ height: '28px', fontSize: '12px' }}
                >
                  Agregar
                </Button>
              </Form.Item>
            </div>
          </div>

          {/* Tabla - 50% del ancho */}
          <div style={{ width: '50%' }}>
            <Table
              columns={columns}
              dataSource={datosTabla}
              pagination={false}
              bordered
              scroll={{ y: 100 }}
              className="custom-table"
              size="small"
              style={{ fontSize: '11px' }}
            />
          </div>
        </div>

        {/* Campos adicionales debajo de la tabla */}
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          marginTop: '12px',
          alignItems: 'flex-start',
          flexWrap: 'wrap'
        }}>
          {/* Select de Retiro */}
          <Form.Item
            label={<span style={{ fontSize: '11px', fontWeight: 500 }}>Retiro</span>}
            name="retiro"
            rules={[{ required: true, message: 'Por favor seleccione el retiro' }]}
            style={{ width: '280px', marginBottom: 0 }}
          >
            <Select
              size="small"
              placeholder="Seleccione retiro"
              showSearch
              optionFilterProp="children"
              loading={loadingPadrones}
              style={{ fontSize: '11px', height: '28px' }}
            >
              {padrones.map(padron => (
                <Option key={padron.id} value={padron.id}>
                  {padron.razon}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Input de Observaciones */}
          <Form.Item
            label={<span style={{ fontSize: '11px', fontWeight: 500 }}>Observaciones</span>}
            name="observaciones"
            style={{ flex: 1, marginBottom: 0 }}
          >
            <Input.TextArea
              size="small"
              placeholder="Ingrese observaciones"
              rows={2}
              style={{ fontSize: '11px' }}
            />
          </Form.Item>
        </div>

        {/* Botones de acción */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '8px',
            marginTop: '12px',
            marginBottom: '12px'
          }}
        >
          <Button
            type="primary"
            size="small"
            onClick={finalizarEgreso}
            loading={enviandoDatos}
            disabled={enviandoDatos}
            style={{ height: '28px', fontSize: '12px' }}
          >
            Finalizar Egreso
          </Button>
          <Button
            size="small"
            onClick={handleCancelar}
            style={{ height: '28px', fontSize: '12px' }}
          >
            Cancelar
          </Button>
          <Button
            size="small"
            onClick={() => {
              // Lógica para ir al listado
              navigate('/listar_egresos');
            }}
            style={{ height: '28px', fontSize: '12px' }}
          >
            Listado
          </Button>
        </div>

        {/* Agregar estilos CSS */}
        <style>
          {`
            .custom-table .ant-table-body {
              min-height: 120px;  /* Altura fija para 3 líneas */
              max-height: 120px;  /* Altura fija para 3 líneas */
            }
            
            .custom-table .ant-table-thead > tr > th {
              background-color: #f5f5f5 !important;
              font-weight: bold;
              position: sticky;
              top: 0;
              z-index: 1;
            }
            
            .custom-table .ant-table-thead > tr > th.ant-table-cell {
              background-color: #f5f5f5 !important;
            }
            
            .custom-table .ant-table-thead > tr:hover > th {
              background-color: #f0f0f0 !important;
            }
            
            .stock-input .ant-input-number-input {
              text-align: right !important;
              font-weight: bold !important;
              background-color: #f5f5f5 !important;
            }
            
            .stock-input.ant-input-number-disabled {
              background-color: #f5f5f5 !important;
              border-color: #d9d9d9 !important;
            }
          `}
        </style>
      </Form>
    </div>
  );
}

export default CrearEgresos;
